/**
 * Superadmin acting-user selection: maps {@link getUserByEmail} results into picker/impersonation shape
 * and normalizes role-like fields from the profile body.
 *
 * Directory rows for the picker come from {@link fetchTenantUsersForPicker} (`getAllUsers`).
 */

import { getUserByEmail, normalizeRolesFromUserRecord } from "./userApi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

/**
 * Re-export user directory for the acting-user modal (same module surface as before).
 */
export { fetchTenantUsersForPicker as fetchActingPickerUserRows } from "./userApi";

/**
 * Extracts normalized AUTH_ROLES-style strings from getUser JSON when the API returns role lists
 * under legacy keys (`groups`, `cognito_groups`, etc.). Delegates to {@link normalizeRolesFromUserRecord}.
 *
 * When `body.role` exists, extend normalization in {@link normalizeRolesFromUserRecord}.
 *
 * @param {object} body - Parsed getUser JSON body
 * @returns {string[]} Lowercase role identifiers
 */
export function normalizeActingUserRolesFromGetUserBody(body) {
  return normalizeRolesFromUserRecord(body);
}

/**
 * @param {{ email: string, expectedCustomerId: string }} params
 * @returns {Promise<{ userId: string, email: string, displayName: string, customerId: string, roles: string[] }|null>}
 */
export async function lookupUserByEmailForActing({ email, expectedCustomerId }) {
  const em = String(email ?? "")
    .trim()
    .toLowerCase();
  const cust = String(expectedCustomerId ?? "").trim();
  if (!EMAIL_RE.test(em) || !cust) return null;

  try {
    const { ok, body } = await getUserByEmail(em, cust);
    if (!ok || !body) return null;

    const bodyCustomer = String(body.customer ?? "").trim();
    if (bodyCustomer && cust && bodyCustomer !== cust) return null;

    const first = String(body.first_name ?? body.firstname ?? body.firstName ?? "").trim();
    const last = String(body.last_name ?? body.lastname ?? body.lastName ?? "").trim();
    const displayName = `${first} ${last}`.trim() || String(body.name ?? "").trim() || String(body.email ?? em).trim();

    const outEmail = String(body.email ?? em)
      .trim()
      .toLowerCase();
    const userId = String(body.username ?? body.user_id ?? body.id ?? "").trim();
    const resolvedUserId = userId || outEmail;
    if (!resolvedUserId && !outEmail) return null;

    const roles = normalizeActingUserRolesFromGetUserBody(body);

    return {
      userId: resolvedUserId,
      email: outEmail,
      displayName,
      customerId: bodyCustomer || cust,
      roles,
    };
  } catch {
    return null;
  }
}
